import { useMemo } from 'react';
import { useLabels } from './use-labels';

export interface ClassificationLabelInfo {
  displayName: string;
  color: string | null;
  sortOrder: number;
  labelCategory: string;
}

export type ClassificationLabelMap = Map<string, ClassificationLabelInfo>;

/**
 * Returns a Map<slug, { displayName, color, sortOrder, labelCategory }> for
 * resolving classification slugs. Used for badges, tab primary selection, and
 * category-aware UI (Type vs Tags split).
 */
export function useClassificationLabelMap(): ClassificationLabelMap {
  const { data: labels } = useLabels();

  return useMemo(() => {
    const map = new Map<string, ClassificationLabelInfo>();
    if (!labels) return map;
    for (const label of labels) {
      map.set(label.name, {
        displayName: label.displayName,
        color: label.color,
        sortOrder: label.sortOrder,
        labelCategory: label.labelCategory,
      });
    }
    return map;
  }, [labels]);
}

/**
 * Return the primary (document_type) label from a label set, or null.
 * Shared with the server's selectPrimaryLabel — keep semantics aligned.
 */
export function getPrimaryFromMap(
  labels: readonly string[],
  map: ClassificationLabelMap
): string | null {
  for (const slug of labels) {
    if (map.get(slug)?.labelCategory === 'document_type') return slug;
  }
  return null;
}

/**
 * Format a slug like "medical_record" to "Medical Record".
 */
export function formatSlug(slug: string): string {
  return slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
